import * as React from 'react';
import {
    allSupportedIntegrationTypesForCapability,
    comingSoonIntegrationsForCapability,
    displayTextForCapability,
    isIntegrationBuildDistroSupported,
} from '../../helpers';
import { IntegrationCapability, IntegrationType } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { IntegrationCard } from './IntegrationCard';
import { ComingSoonIntegrationCard } from './ComingSoonIntegrationCard';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { CardBase } from './CardBase';
import { ToolFilled, ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { createQuickStartCIStepRoute } from '../../helpers/routeHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { trackQuickStartCIEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { RootState } from '../../reducers/reducers';
import { selectIsRunwayOrg } from '../../selectors/selectors';
import { SetupStepsCallerContext } from '../../typescriptHelpers';
import { CreateIntegrationRequestDataType } from '../../actions/integrations';

interface CapabilitySectionProps {
    appId: string;
    orgId: string;
    capability: IntegrationCapability;
    onClickIntegrationShowInstallationModal: (integrationType: IntegrationType) => void;
    onClickShowProviderModal: (integrationType: IntegrationType) => void;
    addIntegration: (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => void;
    shouldShowTitle?: boolean;
    filterIntegrations?: (integration: IntegrationType) => boolean;
    context?: SetupStepsCallerContext;
}

export const CapabilitySection = (props: CapabilitySectionProps) => {
    const {
        appId,
        orgId,
        capability,
        onClickIntegrationShowInstallationModal,
        onClickShowProviderModal,
        addIntegration,
        shouldShowTitle,
        filterIntegrations,
        context,
    } = props;
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));
    let integrationsForCapability = allSupportedIntegrationTypesForCapability(capability, isRunwayOrg, orgId, true);

    const dispatch = useDispatch();
    const history = useHistory();
    if (filterIntegrations) {
        integrationsForCapability = integrationsForCapability.filter(filterIntegrations);
    }

    const capabilityDisplayName = displayTextForCapability(capability);
    const comingSoonIntegrations = comingSoonIntegrationsForCapability(capability, isRunwayOrg, orgId);

    const shouldShowQuickStartCICTA = capability === IntegrationCapability.BuildSystem;
    const isBuildSystemCapabilityBuildDistroContext =
        capability === IntegrationCapability.BuildSystem &&
        context === SetupStepsCallerContext.BuildDistroConnectCICDIntegration;

    if (isBuildSystemCapabilityBuildDistroContext) {
        // sort integrations so supported ones are at the top
        integrationsForCapability.sort((a) => (isIntegrationBuildDistroSupported(a) ? -1 : 1));
    }

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
                {shouldShowTitle && (
                    <PlanRestrictedFeatureWrapper
                        integrationCapability={capability}
                        requiresTooltip
                        shouldFakeDisabledState
                    >
                        <Text style={{ fontSize: 24, fontWeight: 600, color: antGrey.grey9 }}>
                            {capabilityDisplayName}
                        </Text>
                    </PlanRestrictedFeatureWrapper>
                )}
                <PlanRestrictedFeatureWrapper integrationCapability={capability} shouldFakeDisabledState>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                        {shouldShowQuickStartCICTA && (
                            <CardBase
                                titleNode={
                                    <Space align="center" size={18}>
                                        <span>Runway Quickstart CI/CD</span>
                                        <span>
                                            <Tooltip
                                                key="tooltip"
                                                style={{ width: 21, height: 21 }}
                                                title={
                                                    <Text style={{ color: runwayColors.white }}>
                                                        {'⚙️ Don’t have a CI/CD pipeline for your mobile app yet?'}
                                                        <br />
                                                        <Text
                                                            style={{
                                                                color: antGrey.grey6,
                                                                fontWeight: 400,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            Run our wizard to quickly spin up a basic workflow for major
                                                            CI/CD providers.
                                                        </Text>
                                                    </Text>
                                                }
                                            >
                                                <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 22 }} />
                                            </Tooltip>
                                        </span>
                                    </Space>
                                }
                                iconNode={
                                    <ToolFilled
                                        style={{
                                            fontSize: 21,
                                        }}
                                    />
                                }
                                ctaNode={
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<ArrowRightOutlined />}
                                        style={{
                                            backgroundColor: antGrey.grey8,
                                            border: 'none',
                                            color: 'white',
                                        }}
                                        onClick={() => {
                                            const appRoute = createQuickStartCIStepRoute(orgId, appId);
                                            history.replace(appRoute);

                                            dispatch(
                                                trackQuickStartCIEvent({
                                                    appId,
                                                    orgId,
                                                    eventName: AnalyticEvents.Event.ClickStartQuickStartCI,
                                                    extraData: {
                                                        source: AnalyticEvents.QuickStartCISource.Onboarding,
                                                    },
                                                }),
                                            );
                                        }}
                                    >
                                        Get started
                                    </Button>
                                }
                            />
                        )}
                        {integrationsForCapability.map((integrationType) => {
                            const isComingSoonIntegration = comingSoonIntegrations.includes(integrationType);
                            const disableIntegrationCard =
                                isBuildSystemCapabilityBuildDistroContext ?
                                    !isIntegrationBuildDistroSupported(integrationType)
                                :   false;

                            return isComingSoonIntegration ?
                                    <ComingSoonIntegrationCard
                                        orgId={orgId}
                                        appId={appId}
                                        integrationType={integrationType}
                                    />
                                :   <IntegrationCard
                                        key={integrationType}
                                        integrationType={integrationType}
                                        isConnected={false}
                                        onClickIntegrationShowInstallationModal={
                                            onClickIntegrationShowInstallationModal
                                        }
                                        onClickShowProviderModal={onClickShowProviderModal}
                                        addIntegration={addIntegration}
                                        appId={appId}
                                        orgId={orgId}
                                        disabled={disableIntegrationCard}
                                        context={context}
                                    />;
                        })}
                    </div>
                </PlanRestrictedFeatureWrapper>
            </div>
        </div>
    );
};
